/**
 * Markup for the individual pieces of the chart.
 *
 * These are plain string functions with no DOM or Node dependencies, so the
 * build uses them to emit the static page and the client uses them to create
 * headings when the layout changes. One implementation, one output.
 */

import type { Opcode, StackSignature } from '../model/types.ts';
import { markedTitle, toHex } from '../model/types.ts';
import { immediateNames, renderImmediates } from '../model/immediates.ts';
import { addWordBreaks, tokenise } from '../model/names.ts';
import { CATEGORY_LABELS, categorize } from '../model/categories.ts';
import { proposal, standing } from '../model/proposals.ts';
import { normaliseTag } from '../model/search.ts';
import { summarize } from '../model/summary.ts';
import { tagsFor, tagTokens } from '../model/tags.ts';
import type { ViewItem } from '../model/view.ts';

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * A byte as a bare uppercase pair, the usual convention for a byte dump.
 * Reserving the `0x` form for single values lets the notation itself say which
 * is which: `0xE8` is a number, `E8 01` is a run of bytes.
 */
function hexByte(byte: number): string {
  return byte.toString(16).toUpperCase().padStart(2, '0');
}

/**
 * A run of bytes for the encoding breakdown, each written out in full.
 *
 * `FD 87 02` is the convention for a byte dump and assumes you already know
 * you are reading one. This block is where someone finds that out, so each
 * byte says so: `0xFD 0x87 0x02`. The `0x` is set back in grey — it is
 * notation rather than value, and three of them at full strength would be as
 * much ink as the bytes.
 */
function byteRun(bytes: number[]): string {
  return bytes
    .map((byte) => `<span class="enc-0x">0x</span>${hexByte(byte)}`)
    .join(' ');
}

/**
 * How an instruction is encoded.
 *
 * Shows the actual bytes once, split into their parts with a caption under
 * each, so the correspondence is visible rather than described. Reading it
 * should not require reading it: the shape says which byte is the prefix and
 * which bytes carry the sub-opcode.
 *
 * Everything is hex except one place. A sub-opcode is a u32, not a byte, and
 * writing it in two bases side by side invites transposing them — `232` and
 * `0xE8` do not look like the same number, so nothing catches it if they drift
 * apart. Decimal therefore appears only in the spec-notation line, where the
 * `:u32` tag says what it is. That line is how the binary format section of the
 * specification writes the instruction, so it is also the form to search for.
 */
export function renderEncoding(op: Opcode): string {
  const groups: string[] = [];

  if (op.prefixFor?.length) {
    // A doorway byte. Its encoding is the interesting thing about it — it is
    // the only cell in the chart whose whole meaning is "a number follows" —
    // so the slot that number goes in is drawn rather than described, as an
    // open-ended box in the sub-opcode's own colour and face.
    groups.push(group(byteRun([op.code]), 'prefix', 'prefix'));
    groups.push(
      group(
        'n',
        'sub-opcode<span class="enc-sub">LEB128 u32, 1–5 bytes</span>',
        'subslot',
      ),
    );
  } else if (!op.prefix) {
    groups.push(group(byteRun([op.code]), '1-byte opcode'));
  } else {
    groups.push(group(byteRun([op.bytes[0]!]), 'prefix', 'prefix'));

    const encoded = op.bytes.slice(1);
    groups.push(
      group(
        byteRun(encoded),
        `sub-opcode ${specValue(op.code)}` +
          `<span class="enc-sub">LEB128, ${encoded.length} byte${
            encoded.length > 1 ? 's' : ''
          }</span>`,
        'sub',
      ),
    );
  }

  // Immediate operands are part of how the instruction is encoded, so they
  // belong in this picture — but they are variable length and are described
  // rather than enumerated, hence the different treatment.
  //
  // No cross-reference under the caption any more. It used to read "see
  // “Followed by”", which sent a reader from an unexplained letter to a
  // heading that did not say what it held; the section below is now called
  // the same two words as this caption, so the two identify each other.
  const names = immediateNames(op);
  if (names) groups.push(group(escapeHtml(names), 'immediate operands', 'imm'));

  return `<h4>Encoding</h4><div class="encoding"><div class="enc-row">${groups.join('')}</div></div>`;
}

/*
 * One spelling of an opcode, used everywhere it appears — cells, the encoding
 * breakdown, the detail heading. A hex prefix and a decimal sub-opcode are
 * different kinds of number, so they get different colours, and those are the
 * same two colours the encoding breakdown tints its byte groups with. Once you
 * have learnt which is which in one place you have learnt it everywhere.
 */

/** The prefix byte: `0xFD`. */
function hexPart(text: string): string {
  return `<span class="op-hex">${text}</span>`;
}

/**
 * Hex literals inside prose: the same monospace face the chart sets an opcode's
 * own bytes in, in the body colour rather than the opcode colour.
 *
 * The face is what makes `0x40` read as a byte value in the middle of a
 * sentence. The colour is what says it is not this instruction's opcode — the
 * hex in a description is usually some other byte entirely, a blocktype tag or
 * an alignment bit, and tinting it like a cell would claim otherwise.
 *
 * Derived rather than written into the data. There are 41 of them across the
 * descriptions and the notes, and a span per literal is 41 chances to forget
 * one, or to spell the digits in lower case as one entry did.
 *
 * The alternation matches a whole tag first, so a literal is never rewritten
 * inside an attribute value and a tag is never taken apart.
 */
export function markHex(html: string): string {
  return html.replace(/<[^>]+>|0x([0-9A-Fa-f]+)\b/g, (match, digits: string | undefined) =>
    digits === undefined
      ? match
      : `<span class="prose-hex">0x${digits.toUpperCase()}</span>`,
  );
}

/** The sub-opcode, in decimal, with the type tag that says it is not a byte. */
function decPart(code: number): string {
  return `<span class="op-dec">${code}</span><span class="op-utype">:u32</span>`;
}

function specValue(code: number): string {
  return decPart(code);
}

/** The full spec form of an instruction's opcode: `0xFD 263:u32`, or `0x28`. */
export function specLabel(op: Opcode): string {
  if (!op.prefix) return hexPart(`0x${toHex(op.code)}`);
  return `${hexPart(`0x${op.prefix}`)} ${decPart(op.code)}`;
}

/** The same, as plain text, for aria-labels and tooltips. */
export function specText(op: Opcode): string {
  return op.prefix ? `0x${op.prefix} ${op.code}:u32` : `0x${toHex(op.code)}`;
}

/**
 * What to call an opcode in running text — a tooltip, the line under the map,
 * a spoken label.
 *
 * Not `op.name`, quite. The four doorway bytes carry `twobytefd.simd` and the
 * like, placeholders from the old data that were never meant to be read, and
 * an unassigned slot carries nothing at all.
 */
export function plainName(op: Opcode): string {
  if (op.prefixFor?.length) return 'opcode prefix';
  return op.name ?? 'unassigned';
}

function group(bytes: string, caption: string, part = 'opcode'): string {
  return (
    `<span class="enc-group" data-part="${part}">` +
    `<span class="enc-bytes">${bytes}</span>` +
    `<span class="enc-caption">${caption}</span>` +
    `</span>`
  );
}

/**
 * Splits a drawn fragment into its words and the punctuation between them.
 *
 * The dots and underscores of a name belong to no one word — they join two —
 * so a highlight paints the words and leaves them alone. Without this, hovering
 * the `load` of `i64.load16_s` lit `.load`, and a name the parser could not
 * decompose at all, like `return_call_indirect`, lit as one unbroken block of
 * colour with its underscores in it.
 *
 * The `<wbr>` hints sit where `addWordBreaks` used to put them: after a dot,
 * before an underscore. That is where these names want to wrap.
 */
function renderFragment(text: string): string {
  return text
    .split(/([._ ])/)
    .filter(Boolean)
    .map((piece) => {
      if (piece === '.') return `<span class="part-sep">.</span><wbr>`;
      if (piece === '_') return `<wbr><span class="part-sep">_</span>`;
      // The space in `ref.test null`, which separates two words as much as an
      // underscore does and should no more be lit than one.
      if (piece === ' ') return `<span class="part-sep"> </span><wbr>`;
      return `<span class="part-word">${addWordBreaks(escapeHtml(piece))}</span>`;
    })
    .join('');
}

/**
 * The instruction name, split into its parts so each can be styled and so long
 * names get sensible break opportunities.
 *
 * The `.op` and `.post` spans carry a CSS-generated newline, which renders as a
 * line break but is not picked up when the text is copied — so copying a cell
 * still yields `i64.load16_u` on one line.
 */
export function renderName(op: Opcode): string {
  // Not `.op`: that class carries the generated line break and the weight that
  // belong to a decomposed instruction name, and a doorway byte's cell is not
  // one — it is a small block of its own with its own arrangement.
  if (op.displayName) return `<span class="display-name">${op.displayName}</span>`;
  if (!op.name) return '';

  const tokens = tokenise(op.parts, op.name);
  return tokens
    .map((t, i) => {
      const cls = t.first ? t.role : `${t.role}-x`;
      // Between spans rather than inside them: the separators are where the
      // name wants to wrap, and each span is now one whole part.
      const wbr = i > 0 ? '<wbr>' : '';
      return (
        `${wbr}<span class="${cls}" data-p="${escapeHtml(t.token)}">` +
        `${renderFragment(t.text)}</span>`
      );
    })
    .join('');
}

/** The distinct tokens of a name, for matching a hovered part across cells. */
export function partTokens(op: Opcode): string {
  if (!op.name) return '';
  return [...new Set(tokenise(op.parts, op.name).map((t) => t.token))].join(' ');
}

/** Attributes that let the highlight logic select related cells with plain CSS. */
function cellData(op: Opcode): string {
  // As with its properties: the parts of `twobytefd.simd` are not parts of
  // anything, and relating this cell to every SIMD instruction because of them
  // answers a question nobody asked.
  if (op.prefixFor?.length) {
    // Except which group it is in, which is a fact about the byte rather than
    // about its placeholder name: "Opcode prefix" is what these four are, and it
    // carries their colour and the chip that lights all four at once.
    const from = op.proposal ? ` data-proposal="${escapeHtml(op.proposal)}"` : '';
    // "Prefix" is a heading in the card layout and a chip in the panel, and a
    // chip that lights nothing is worse than no chip: the four bytes answer to
    // it like every other category answers to its own.
    const cat = categorize(op);
    const tags = [`cat-${cat}`, op.proposal ? `from-${op.proposal}` : '']
      .filter(Boolean)
      .join(' ');
    return (
      ` data-key="${op.id}" data-section="${op.section}" data-status="${op.status}"` +
      ` data-cat="${cat}"${from} data-tags="${escapeHtml(tags)}"`
    );
  }
  const attrs: Record<string, string | undefined> = {
    'data-key': op.id,
    'data-section': op.section,
    'data-status': op.status,
    'data-cat': categorize(op),
    'data-pre': op.parts?.pre,
    'data-op': op.parts?.mainop,
    'data-bits': op.parts?.opbits,
    'data-post': op.parts?.post,
    'data-sign': op.parts?.sign,
    'data-tags': op.name ? tagTokens(op) : undefined,
    'data-parts': op.name ? partTokens(op) : undefined,
    'data-proposal': op.proposal,
  };
  return Object.entries(attrs)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([name, value]) => ` ${name}="${escapeHtml(value!)}"`)
    .join('');
}

/**
 * Whether an opcode has anything worth writing a detail section for. An
 * unassigned slot does not: saying "0xA7 is unassigned" 234 times costs more
 * than it tells anyone, and the panel can state it without help.
 */
export function hasDetail(op: Opcode): boolean {
  return Boolean(
    op.name || op.description || op.stack || op.immediates?.length || op.linkTo,
  );
}

export function renderCell(op: Opcode, filtered = false): string {
  // A documented cell is a link to its own detail section. With JavaScript that
  // opens the side panel; without it, the link still navigates to the full
  // description. Unassigned slots have no target, so they are inert.
  const inner = op.linkTo
    ? op.linkTo.map((l) => `<a class="jump" href="${escapeHtml(l.href)}">${l.label}</a>`).join('')
    : renderName(op);

  const linkable = hasDetail(op) && !op.linkTo;
  const tag = linkable ? 'a' : 'div';
  const href = linkable ? ` href="#${op.id}"` : '';
  const hidden = filtered ? ' data-filtered="1"' : '';
  // A doorway byte's `name` is a placeholder from the old data — `twobytefb.gc`
  // — which is not something to read out. What it is, is a prefix.
  const spoken = op.prefixFor?.length
    ? `${specText(op)} opcode prefix, a sub-opcode follows`
    : op.name
      ? `${specText(op)} ${op.name}`
      : '';
  const label = spoken ? ` aria-label="${escapeHtml(spoken)}"` : '';

  // The extra columns are only shown by the table layout, but they are written
  // here rather than injected later: a cell is rendered once at build time and
  // then only ever moved, which is what lets the animation follow it.
  const summary = summarize(op);
  const columns =
    `<span class="cell-summary">${summary ? escapeHtml(summary) : ''}</span>` +
    `<span class="cell-cat">${op.name ? CATEGORY_LABELS[categorize(op)] : ''}</span>` +
    `<span class="cell-status">${escapeHtml(statusBrief(op))}</span>`;

  return (
    `<${tag} class="cell"${href}${cellData(op)}${hidden}${label}>` +
    `<span class="cell-hex">${specLabel(op)}</span>` +
    `<span class="cell-name">${inner}</span>` +
    columns +
    `</${tag}>`
  );
}

/** The proposals represented in a group, as chips that light up their own. */
function renderGroupProposals(ids: string[] | undefined): string {
  if (!ids?.length) return '';
  const chips = ids
    .map((id) => proposal(id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map(
      (p) =>
        `<button type="button" class="tag" data-tag="from-${p.id}" data-kind="status" ` +
        `data-proposal="${escapeHtml(p.id)}">${escapeHtml(p.name)}</button>`,
    )
    .join('');
  return chips ? `<span class="group-proposals">${chips}</span>` : '';
}

/**
 * A heading that is also its own property chip.
 *
 * A group heading names a property every instrument under it shares, which is
 * the same thing a chip in the panel names — so clicking it lights the same
 * cells, including the ones scrolled far away or in another section. Headings
 * for groupings that are not properties (the byte-grid sections) stay plain
 * text.
 */
function headingTag(label: string, tag: string | undefined): string {
  if (!tag) return escapeHtml(label);
  return (
    `<button type="button" class="tag heading-tag" data-tag="${escapeHtml(tag)}" ` +
    `data-kind="category" title="${escapeHtml(tagHint(label))}">${escapeHtml(label)}</button>`
  );
}

/**
 * Clicking a tag highlights; searching it filters. The two are worth having
 * separately — a highlight keeps the chart intact so you can see *where* the
 * matches are, a filter clears everything else away — and the tooltip is where
 * the second one is discoverable, since nothing about a chip suggests you
 * could type it.
 */
function tagHint(label: string): string {
  return `Highlight everything tagged “${label}” — or search tag:${normaliseTag(label)} to filter`;
}

/**
 * How many instructions are under a heading.
 *
 * A bare number beside a heading is a riddle — 200 of what? — so it says what
 * it counts, in every layout. The byte grid used to hide the word to save the
 * width, which saved four characters on a heading that spans sixteen columns
 * and left the one place with room to spare as the one place that did not say
 * what it meant.
 *
 * A count of one is not worth a badge at all. Grouping by name gives a heading
 * per operation and a good many operations exist exactly once; "1" against each
 * of them is a column of noise saying nothing the heading did not.
 *
 * Nor is a count of none. The four doorway bytes are not instructions and are
 * not counted as any, so grouping by name gives each of them a heading of its
 * own over a single cell reading "0 opcodes" — which is true of what it counts
 * and reads as a group that lost its contents.
 */
function renderCount(count: number): string {
  return (
    `<span class="group-count"${count <= 1 ? ' hidden' : ''}>` +
    `<span class="count-n">${count}</span>` +
    `<span class="count-unit"> opcode${count === 1 ? '' : 's'}</span></span>`
  );
}

export function renderItem(item: ViewItem): string {
  switch (item.kind) {
    case 'group':
      return (
        `<h2 class="group" data-key="${escapeHtml(item.key)}" id="${escapeHtml(item.anchor)}">` +
        // A section heading carries its table's letter as the mark inside its
        // own title; a category heading is a property chip and carries neither.
        (item.mark
          ? markedTitle({ title: item.label, mark: item.mark })
          : headingTag(item.label, item.tag)) +
        ` ${renderCount(item.count)}` +
        (item.intro ? `<span class="group-intro">${item.intro}</span>` : '') +
        renderGroupProposals(item.proposals) +
        `</h2>`
      );
    case 'subgroup':
      return (
        `<h3 class="subgroup" data-key="${escapeHtml(item.key)}">` +
        headingTag(item.label, item.tag) +
        `</h3>`
      );
    case 'tablehead':
      return (
        `<div class="tablehead" data-key="${escapeHtml(item.key)}" aria-hidden="true">` +
        `<span>Opcode</span><span>Instruction</span><span>Category</span>` +
        `<span>Status</span>` +
        `</div>`
      );
    case 'corner':
      return (
        `<div class="corner" data-key="${escapeHtml(item.key)}" ` +
        `title="Row and column are the sub-opcode in hex">${escapeHtml(item.label)}</div>`
      );
    case 'colhead':
      return (
        `<div class="colhead" data-key="${escapeHtml(item.key)}" ` +
        `data-notation="${item.notation}">${escapeHtml(item.label)}</div>`
      );
    // Empty on purpose: it is a gap, and a gap says what it has to say by
    // being there. Hidden from anything reading the page as a document, which
    // has the numbers themselves and no rows to lose its place among.
    case 'band':
      return `<div class="band" data-key="${escapeHtml(item.key)}" aria-hidden="true"></div>`;
    case 'rowhead':
      return (
        `<div class="rowhead" data-key="${escapeHtml(item.key)}" ` +
        `data-notation="${item.notation}">${escapeHtml(item.label)}</div>`
      );
    case 'cell':
      return renderCell(item.op, item.filtered ?? false);
  }
}

/**
 * How each status is announced: a mark, a label and a year.
 *
 * There used to be a sentence as well — "not standardised. Engine support
 * varies and the encoding may still change." — which said in twenty words
 * roughly what the proposal's own note two lines below said in its own terms,
 * and said it identically over a phase-1 sketch and a phase-3 design that
 * engines have already shipped. Whatever those sentences knew that the notes
 * did not has been folded into the notes; what is left here is the part that
 * differs from one instruction to the next.
 *
 * Which is the phase and the year. "Phase 3" alone does not say whether the
 * design is moving; "Phase 3 (2026)" and "Phase 1 (2022)" are two different
 * pieces of advice. What the year *is* depends on the status — reaching a
 * phase, going quiet, being abandoned — so the tooltip says which, and every
 * one of them is a date from the proposal's own record rather than a guess.
 */
interface StatusNote {
  mark: string;
  label: string;
  /** The tooltip: what happened in that year. */
  title?: string;
  /** Set on the one status that is reassurance rather than caution. */
  settled?: boolean;
}

const MARK = {
  proposal: '\u{1F9EA}',
  legacy: '\u26A0\uFE0F',
  dormant: '\u{1F4A4}',
  withdrawn: '\u26D4',
  settled: '\u2705',
} as const;

function statusNote(op: Opcode): StatusNote | undefined {
  const p = proposal(op.proposal);
  const year = p?.since ? ` (${p.since})` : '';
  const phase = ` Phase ${p?.phase ?? '?'}`;
  const title = p?.sinceNote ? { title: p.sinceNote } : {};

  switch (op.status) {
    case 'proposal':
      /*
       * A phase-4 proposal is finished in every sense a reader cares about: the
       * design is settled, the encoding is fixed and the engines have shipped
       * it. The atomics are the case in point \u2014 telling someone that
       * `i32.atomic.load` "may still change" is not caution, it is wrong, and it
       * makes the same warning on a phase-1 instruction mean less.
       */
      return p?.phase === 4
        ? { mark: MARK.settled, label: `Phase 4${year}`, settled: true, ...title }
        : { mark: MARK.proposal, label: `Proposal:${phase}${year}`, ...title };
    case 'legacy':
      return { mark: MARK.legacy, label: `Legacy${year}`, ...title };
    // The phase belongs on this one too: dormant is not a phase of its own, it
    // is a proposal that stopped somewhere, and where it stopped is the fact.
    case 'dormant':
      return { mark: MARK.dormant, label: `Dormant:${phase}${year}`, ...title };
    case 'withdrawn':
      return { mark: MARK.withdrawn, label: `Withdrawn${year}`, ...title };
    default:
      return undefined;
  }
}

/**
 * The same standing, in a column: `Wasm 2.0`, `Proposal (Phase 3)`,
 * `Withdrawn`.
 *
 * The list shows one instruction per row and had no room to say the thing that
 * most changes what a row means — whether the byte is settled, still moving, or
 * gone. The columns it did have were the immediates and the stack type, both of
 * which are notation the panel draws properly and neither of which reads well
 * squeezed into a few rems.
 *
 * No emoji, unlike the panel's box. A mark is worth having where it is one
 * caution against a page of prose; six hundred of them down a column is a
 * pictogram per row saying what the words beside it already say.
 *
 * No year either, which the panel does give. A year is what tells you whether a
 * proposal is moving or has stalled, and that is a thing to read once about the
 * one instruction you opened — not six hundred times down a column, where every
 * row carries four digits that are the same as the four above them and answer a
 * question nobody asked of a list.
 *
 * "Wasm" rather than "WebAssembly" because the release number is the fact and
 * the word is the same on every standardised row — which is most of them — and
 * a column of it would set the width for nothing. The proposals say "Proposal"
 * in full: a bare "Phase 3" under a heading reading Status is a scale with no
 * name, and phases belong to proposals rather than to the language.
 */
export function statusBrief(op: Opcode): string {
  // Not an instruction, so it has no standing; and its "proposal" is the group
  // the four doorway bytes are filed under rather than a history.
  if (op.prefixFor?.length) return '';
  const p = proposal(op.proposal);

  switch (op.status) {
    case 'legacy':
      return 'Legacy';
    case 'withdrawn':
      return 'Withdrawn';
    case 'dormant':
      return 'Dormant proposal';
    case 'proposal':
      // A proposal that stopped is described by having stopped. Which phase it
      // stopped at is in the panel; here it would read as progress.
      if (p?.stage === 'dormant') return 'Dormant proposal';
      return p?.phase ? `Proposal (Phase ${p.phase})` : 'Proposal';
    default:
      if (!p?.standardisedIn) return '';
      return p.standardisedIn.replace('WebAssembly', 'Wasm').replace(/\s*\(\d{4}\)/, '');
  }
}

/**
 * The opcode's name as a panel heading, with its opcode and its immediate
 * operands.
 *
 * An instruction is identified by two things and the heading used to give one
 * of them. The bytes were in the encoding breakdown below, drawn rather than
 * written, which is the right way to explain an encoding and the wrong way to
 * label a section — and anything that reads the page as a document rather than
 * as a page (Chrome's reading mode, a text extractor, a screen reader skimming
 * headings) got a list of names with no numbers against them.
 *
 * `h2` rather than `h3` because the sections under it are `h4`. Two levels
 * apart reads as a title over its parts; one level apart reads as a list of
 * five equal things, the first of which happens to be a name.
 */
export function renderHeading(op: Opcode): string {
  /*
   * A doorway byte is titled as what it is.
   *
   * Its `displayName` is the block drawn in its cell — a range, a count badge
   * and a row of emoji, laid out for sixty pixels of byte grid — and using
   * that as the panel's title put a small centred table where the heading
   * should be, above a description that then said the same three facts again
   * in sentences. And its `name` is `twobytefd.simd`, a placeholder from the
   * old data that is not a name and should not be read out.
   */
  if (op.prefixFor?.length) {
    return (
      `<h2 class="detail-name">${specLabel(op)}` +
      `<span class="detail-role">opcode prefix</span></h2>` +
      // Generated with the rest of what a doorway says about itself, since the
      // last sentence names the tables it opens; see `summarise` in prefixes.
      `<p class="detail-summary">${op.prefixSummary ?? ''}</p>`
    );
  }

  const name = op.displayName ?? (op.name ? escapeHtml(op.name) : '<em>Unassigned</em>');
  const summary = summarize(op);
  return (
    // A doorway byte's cell already opens with its own byte, larger.
    `<h2 class="detail-name">` +
    (op.prefixFor?.length ? '' : `<span class="detail-op">${specLabel(op)}</span>`) +
    name +
    (immediateNames(op)
      ? ` <span class="immediate-args">${escapeHtml(immediateNames(op))}</span>`
      : '') +
    `</h2>` +
    (summary ? `<p class="detail-summary">${escapeHtml(summary)}</p>` : '')
  );
}

/**
 * Where an instruction came from, and where that proposal got to.
 *
 * The text belongs to the proposal, not to the instruction, so it is looked up
 * rather than repeated. The old page pasted "Reference Types Proposal" into
 * dozens of descriptions; when the proposal finished, every one of them became
 * wrong at the same moment.
 */
/**
 * The feature table on webassembly.org: which engines and tools have shipped
 * each proposal, kept current in a way a chart reviewed once a year cannot be.
 *
 * The link points at the row by its text rather than by its id. The ids there
 * are positional — `feat-row-header-21` — so a link to one would quietly start
 * pointing at a different feature the next time a proposal is added, whereas
 * the name is what actually identifies the row.
 *
 * Tested end to end, and today the highlight does not fire: that table is
 * built by JavaScript after the page loads, and the browser looks for the text
 * before it exists. (An id fragment does scroll, because fragment scrolling is
 * retried as content arrives; text fragments are not.) The link still lands on
 * the right page, which is where it went before, and it starts working by
 * itself if webassembly.org ever renders that table server-side. Firefox does
 * not implement text fragments at all.
 */
const SUPPORT_URL = 'https://webassembly.org/features/';

function supportLink(feature: string): string {
  // A text fragment uses `-` to mark a prefix or suffix, so a hyphen in the
  // name itself has to be escaped. encodeURIComponent leaves it alone.
  return `${SUPPORT_URL}#:~:text=${encodeURIComponent(feature).replace(/-/g, '%2D')}`;
}

/**
 * Where an instruction stands: whether the byte means today what the cell says
 * it means, and which proposal it came through.
 *
 * The two were separate — a caution near the top of the panel, and a "History"
 * section further down — which put the same question in two places. "This is
 * still a proposal" and "it is the stack-switching proposal, at phase 3" are
 * one answer, so they are one section, and it is called what it answers.
 */
export function renderStatus(op: Opcode, spread?: string): string {
  // A doorway byte is not an instruction and has no standing of its own. It is
  // filed under "Opcode prefix" so that it has a colour and a chip like every
  // other run of bytes, and that is a grouping rather than a history: a Status
  // section here would be four lines saying it is a byte.
  if (op.prefixFor?.length) return '';

  const note = statusNote(op);
  const p = proposal(op.proposal);
  if (!note && !p) return '';

  const caution = note
    ? `<p class="detail-status" data-status="${op.status}"` +
      (note.settled ? ` data-tone="settled"` : '') +
      (note.title ? ` title="${escapeHtml(note.title)}"` : '') +
      `>` +
      `<span class="status-mark" aria-hidden="true">${note.mark}</span>` +
      `<span class="status-text"><strong>${note.label}</strong>` +
      (op.supersededBy
        ? `<span class="detail-superseded">Replaced by ${op.supersededBy}</span>`
        : '') +
      `</span></p>`
    : '';

  if (!p) return `<h4>Status</h4>${caution}`;

  // Only where the table has a row to point at. The original release is not a
  // feature anyone opts into, and the abandoned draft encodings never were.
  const support = p.feature
    ? `<a class="history-support" href="${escapeHtml(supportLink(p.feature))}" ` +
      `title="Which engines and tools have shipped “${escapeHtml(p.feature)}”, on webassembly.org">` +
      `Engine support</a>`
    : '';
  return (
    `<h4>Status</h4>` +
    // The proposal first, then the caution, and the caution against the name
    // rather than at the foot of the section. Which proposal an instruction came
    // through is the answer to "what is this" and where it has got to is the
    // qualification of that answer, so the two are one line and a box under it;
    // the note and the support link are elaboration and follow.
    //
    // A div rather than a p, since a p cannot hold the box.
    `<div class="detail-history">` +
    `<a class="history-name" href="${escapeHtml(p.url)}">${escapeHtml(p.name)}</a>` +
    // Only where the box is not already saying it. "Proposal, phase 3" directly
    // above "Proposal: Phase 3 (2026)" is one fact printed twice, and the one
    // with the year is the better of the two. A standardised proposal has no
    // box, and this is where it says which release folded it in.
    (note ? '' : `<span class="history-standing">${escapeHtml(standing(p))}</span>`) +
    caution +
    `<span class="history-note">${escapeHtml(p.note)}</span>` +
    support +
    `</div>` +
    // Only for the proposals that reach into more than one table; see
    // `proposalSpread`.
    (spread ?? '')
  );
}

/**
 * The property chips. Each is a button rather than a link: it does not go
 * anywhere, it lights up everything sharing that property.
 */
export function renderTags(op: Opcode): string {
  // A doorway byte has no properties: the ones it was being given — "Other",
  // "simd" — are read off `twobytefd.simd`, which is a placeholder standing in
  // for a name it does not have.
  if (op.prefixFor?.length) return '';
  const tags = tagsFor(op);
  if (!tags.length) return '';
  return (
    `<h4>Properties</h4><p class="detail-tags">` +
    tags
      .map(
        (tag) =>
          `<button type="button" class="tag" data-tag="${escapeHtml(tag.id)}" ` +
          `data-kind="${tag.kind}" title="${escapeHtml(tagHint(tag.label))}">` +
          `${escapeHtml(tag.label)}</button>`,
      )
      .join('') +
    `</p>` +
    // The chips are buttons, and anything that reads the page as a document
    // throws buttons away — which left a Properties heading with nothing under
    // it, the one section that looked broken rather than absent. The same list
    // as plain text, off screen, and hidden from screen readers because they
    // read the chips perfectly well already.
    `<p class="reading-text" aria-hidden="true">` +
    escapeHtml(tags.map((tag) => tag.label).join(', ')) +
    `.</p>`
  );
}

/**
 * What a polymorphic signature means, said once here rather than copied onto
 * each instruction.
 *
 * The old page wrote the first of these as "**stack-polymorphic**: performs an
 * *unconditional control transfer*", which announces a term of art and then
 * restates it in more jargon, and the second as the bare fragment
 * "(value-polymorphic)", a term defined nowhere on the page.
 */
export function polymorphicNote(kind: StackSignature['polymorphic']): string {
  switch (kind) {
    case 'stack':
      return (
        '<p><b>Stack-polymorphic</b>: execution jumps away and never continues past this ' +
        'instruction, so the validator accepts whatever is left on the stack.</p>'
      );
    case 'value':
      return (
        '<p><b>Value-polymorphic</b>: works on any value type — <i>t</i> is whatever the ' +
        'operands are.</p>'
      );
    default:
      return '';
  }
}

/**
 * The written description of one opcode — the part that cannot be derived and
 * so has to be shipped. The byte sequence and encoding breakdown are not here:
 * they follow from the prefix and code, and baking 630 copies of the same table
 * into the page would be repeating what the client can work out.
 */
export function renderDetail(op: Opcode, spread?: string): string {
  const rows: string[] = [renderHeading(op)];
  const tags = renderTags(op);

  if (op.description) {
    rows.push(`<h4>Description</h4><div class="detail-prose">${markHex(op.description)}</div>`);
  }

  // Above the status. What an instruction does to the stack is the fact most
  // readers came for; where its proposal got to is the qualification of that
  // answer, and on the settled majority it says the same thing every time.
  if (op.stack) {
    rows.push(
      `<h4>Stack</h4><p><span class="op-type">${op.stack.html}</span></p>` +
        (op.stack.polymorphic || op.stack.note
          ? `<div class="detail-stack-note">${polymorphicNote(op.stack.polymorphic)}${
              markHex(op.stack.note ?? '')
            }</div>`
          : ''),
    );
  }

  // Named for what it holds, and named the same two words the encoding
  // breakdown captions the operand slot with, so the two identify each other.
  // "Followed by" named a relation with the other end missing, directly under
  // a description written in prose.
  const immediates = renderImmediates(op);
  if (immediates) {
    rows.push(
      `<h4>Immediate operands</h4><div class="detail-followed">${markHex(immediates)}</div>`,
    );
  }

  rows.push(renderStatus(op, spread));

  if (tags) rows.push(tags);

  return (
    `<article class="detail" id="${op.id}" data-key="${op.id}">` +
    rows.join('\n') +
    `</article>`
  );
}
